import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { BIZ_REF } from "../seo";
import ServiceExplainer from "../components/ServiceExplainer";
import { company } from "../mock";
import { serviceAreas } from "../mock";
import { comboServiceSlugs } from "../comboPages";
import { getServiceContent, serviceContent } from "../serviceContent";
import ContactForm from "../components/ContactForm";
import FAQ from "../components/FAQ";
import JsonLd from "../components/JsonLd";

const ServiceDetail = () => {
  const { slug } = useParams();
  const svc = getServiceContent(slug);

  useSEO({ title: svc?.metaTitle, description: svc?.metaDesc, canonicalPath: svc?.canonicalPath, image: svc?.image });

  if (!svc) return <Navigate to="/services" replace />;

  const related = Object.entries(serviceContent)
    .filter(([s]) => s !== slug)
    .slice(0, 4)
    .map(([s, v]) => ({ slug: s, name: v.name }));

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://fabbuildpro.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://fabbuildpro.com/services" },
      { "@type": "ListItem", position: 3, name: svc.name, item: `https://fabbuildpro.com/services/${slug}` },
    ],
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: svc.name,
    name: svc.h1,
    url: `https://fabbuildpro.com/services/${slug}`,
    provider: BIZ_REF,
    areaServed: ["Northern Virginia", "Washington D.C.", "Maryland"],
    description: svc.metaDesc,
  };

  return (
    <>
      <JsonLd data={breadcrumbSchema} id="svc-breadcrumb" />
      <JsonLd data={serviceSchema} id="svc-service" />

      {/* Hero */}
      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-slate to-brand-navy" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/services" className="hover:text-brand-orange transition-colors">Services</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-brand-orange">{svc.name}</span>
            </div>
            <h1 className="font-display font-extrabold text-white text-3xl md:text-4xl leading-tight">
              {svc.h1}
            </h1>
            <div className="flex flex-wrap gap-3 mt-7">
              <a href={company.phoneHref} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5">
                <Phone className="w-5 h-5" /> Call {company.phone}
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all">
                Free Estimate <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
            <img src={svc.image} alt={svc.name} className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </section>

      {/* Intro + benefits */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4 text-slate-700 leading-relaxed text-[17px]">
            {svc.intro.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 h-fit">
            <h2 className="font-display font-bold text-brand-navy text-lg mb-4">
              Why choose Fab Build Pro
            </h2>
            <ul className="space-y-3">
              {svc.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-slate-700 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <ServiceExplainer svc={svc} />

      {/* Related services */}
      <section className="bg-brand-sand py-14">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl mb-6">
            Explore Our Other Services
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}`}
                className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
              >
                {r.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQ heading={`${svc.name} FAQs`} items={svc.faqs} />

      {comboServiceSlugs.includes(slug) && (
        <section className="bg-brand-cream py-16" data-testid="service-areas-links">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-3">
              {svc.name} Near You
            </h2>
            <p className="text-slate-600 mb-8 max-w-xl mx-auto">
              Fab Build Pro provides {svc.name.toLowerCase()} across Northern Virginia, D.C. & Maryland. Find your city:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {serviceAreas.map((a) => (
                <Link
                  key={a.slug}
                  to={`/services/${slug}/${a.slug}`}
                  className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
                >
                  {svc.name} in {a.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactForm />
    </>
  );
};

export default ServiceDetail;
